"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ChevronRight, ListTodo, Loader2 } from "lucide-react";
import { Tag } from "@/components/ui/tag";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useFacility } from "@/contexts/FacilityContext";
import { useRouter } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import {
  OPERATIONS_TODOS_UPDATED_EVENT,
  OperationsDashboardAction,
  statisticsRepository,
} from "@/repository/statisticsRepository";
import {
  CUSTOMER_TASK_UPDATED_EVENT,
  customerTaskRepository,
  type CustomerTask,
} from "@/repository/customerTaskRepository";

const priorityOrder: Record<OperationsDashboardAction["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const priorityClass: Record<
  OperationsDashboardAction["priority"],
  { dot: string; count: string }
> = {
  high: {
    dot: "bg-rose-500",
    count: "border-rose-200 bg-rose-50 text-rose-700",
  },
  medium: {
    dot: "bg-amber-500",
    count: "border-amber-200 bg-amber-50 text-amber-700",
  },
  low: {
    dot: "bg-slate-400",
    count: "border-slate-200 bg-slate-50 text-slate-600",
  },
};

const formatCount = (count: number) =>
  new Intl.NumberFormat("ja-JP").format(count);

const taskPriorityClass: Record<CustomerTask["priority"], string> = {
  HIGH: "bg-rose-500",
  MEDIUM: "bg-amber-500",
  LOW: "bg-slate-400",
};

const taskDueLabel = (task: CustomerTask) => {
  if (!task.dueDate) return null;
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  if (task.dueDate < todayKey) return "期限超過";
  if (task.dueDate === todayKey) return "本日期限";
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
  }).format(new Date(`${task.dueDate}T00:00:00`));
};

export function HeaderTodoPopover() {
  const router = useRouter();
  const {
    selectedFacilityId,
    selectedFacilityName,
    isAllFacilities,
    isLoading: facilityLoading,
  } = useFacility();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<OperationsDashboardAction[]>([]);
  const [memberTasks, setMemberTasks] = useState<CustomerTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const requestIdRef = useRef(0);

  const fetchTodos = useCallback(async () => {
    if (facilityLoading) return;

    const requestId = ++requestIdRef.current;
    setFailed(false);
    const facilityId = isAllFacilities ? undefined : selectedFacilityId;
    const [operationsResult, memberTaskResult] = await Promise.allSettled([
      statisticsRepository.getOperationsTodos(facilityId),
      customerTaskRepository.findForHeader(facilityId),
    ]);
    if (requestId !== requestIdRef.current) return;

    if (operationsResult.status === "fulfilled") {
      setItems(
        operationsResult.value.data.actionItems
          .filter((item) => item.count > 0)
          .sort(
            (a, b) =>
              priorityOrder[a.priority] - priorityOrder[b.priority] ||
              b.count - a.count
          )
      );
    } else {
      setItems([]);
    }

    if (memberTaskResult.status === "fulfilled") {
      setMemberTasks(memberTaskResult.value);
    } else {
      setMemberTasks([]);
    }

    setFailed(
      operationsResult.status === "rejected" &&
        memberTaskResult.status === "rejected"
    );
    setLoading(false);
  }, [facilityLoading, isAllFacilities, selectedFacilityId]);

  useEffect(() => {
    if (facilityLoading) return;

    setItems([]);
    setMemberTasks([]);
    setLoading(true);
    void fetchTodos();

    const handleFocusOrVisible = () => {
      if (document.visibilityState === "visible") {
        void fetchTodos();
      }
    };

    window.addEventListener("focus", handleFocusOrVisible);
    window.addEventListener(CUSTOMER_TASK_UPDATED_EVENT, handleFocusOrVisible);
    window.addEventListener(
      OPERATIONS_TODOS_UPDATED_EVENT,
      handleFocusOrVisible
    );
    document.addEventListener("visibilitychange", handleFocusOrVisible);

    return () => {
      requestIdRef.current += 1;
      window.removeEventListener("focus", handleFocusOrVisible);
      window.removeEventListener(
        CUSTOMER_TASK_UPDATED_EVENT,
        handleFocusOrVisible
      );
      window.removeEventListener(
        OPERATIONS_TODOS_UPDATED_EVENT,
        handleFocusOrVisible
      );
      document.removeEventListener("visibilitychange", handleFocusOrVisible);
    };
  }, [facilityLoading, fetchTodos]);

  const systemCount = useMemo(
    () => items.reduce((sum, item) => sum + item.count, 0),
    [items]
  );
  const totalCount = systemCount + memberTasks.length;

  const handleItemClick = (item: OperationsDashboardAction) => {
    setOpen(false);
    router.push(item.route);
  };

  const handleMemberTaskClick = (task: CustomerTask) => {
    if (!task.route) return;
    setOpen(false);
    router.push(task.route);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) void fetchTodos();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label={`要対応ToDo ${formatCount(totalCount)}件`}
          title="要対応ToDo"
          className="hover:text-brand text-muted-foreground relative h-11 w-11 lg:h-10 lg:w-10"
        >
          <ListTodo className="h-4 w-4" aria-hidden="true" />
          {totalCount > 0 && (
            <span className="pointer-events-none absolute -right-1.5 -top-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-700 px-1.5 text-[10px] font-semibold tabular-nums leading-none text-white ring-2 ring-white">
              {totalCount > 99 ? "99+" : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-border border-b px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-foreground text-sm font-semibold">要対応ToDo</p>
            {!loading && totalCount > 0 && (
              <span className="text-muted-foreground text-xs font-medium">
                {formatCount(totalCount)}件
              </span>
            )}
          </div>
          <p className="text-muted-foreground truncate text-xs">
            {selectedFacilityName}
          </p>
        </div>

        <div className="max-h-[480px] overflow-y-auto">
          {loading && items.length === 0 && memberTasks.length === 0 && (
            <div className="text-muted-foreground flex items-center justify-center gap-2 px-4 py-8">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-sm">要対応を確認しています</p>
            </div>
          )}

          {!loading &&
            failed &&
            items.length === 0 &&
            memberTasks.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-muted-foreground text-sm">
                  要対応を取得できませんでした
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setLoading(true);
                    void fetchTodos();
                  }}
                  className="text-primary mt-2 text-xs font-medium hover:underline"
                >
                  再読み込み
                </button>
              </div>
            )}

          {memberTasks.length > 0 && (
            <section aria-labelledby="member-task-heading">
              <div className="bg-muted/40 border-b px-4 py-2">
                <h3
                  id="member-task-heading"
                  className="text-muted-foreground text-xs font-semibold"
                >
                  会員別タスク
                </h3>
              </div>
              <ul>
                {memberTasks.map((task) => {
                  const dueLabel = taskDueLabel(task);
                  return (
                    <li
                      key={task.id}
                      className="border-b border-gray-100 last:border-0"
                    >
                      <button
                        type="button"
                        onClick={() => handleMemberTaskClick(task)}
                        disabled={!task.route}
                        className="hover:bg-muted/50 group flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left transition-colors disabled:cursor-default disabled:opacity-70"
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            "h-2 w-2 shrink-0 rounded-full",
                            taskPriorityClass[task.priority]
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="sr-only">
                            優先度
                            {task.priority === "HIGH"
                              ? "高"
                              : task.priority === "LOW"
                                ? "低"
                                : "通常"}
                          </span>
                          <span className="text-foreground block truncate text-sm font-medium">
                            {task.title}
                          </span>
                          <span className="text-muted-foreground mt-0.5 block truncate text-xs">
                            {task.targetName}
                            {task.assignedStaff
                              ? `・${task.assignedStaff.name}`
                              : "・担当者なし"}
                          </span>
                        </span>
                        {dueLabel && (
                          <Tag
                            tone="neutral"
                            className={cn(
                              "shrink-0 tabular-nums",
                              (dueLabel === "期限超過" ||
                                dueLabel === "本日期限") &&
                                "border-rose-200 bg-rose-50 text-rose-700"
                            )}
                            size="compact"
                          >
                            {dueLabel}
                          </Tag>
                        )}
                        {task.route && (
                          <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {items.length > 0 && (
            <section aria-label="システムの要対応">
              {memberTasks.length > 0 && (
                <div className="bg-muted/40 border-y px-4 py-2">
                  <h3
                    id="system-todo-heading"
                    className="text-muted-foreground text-xs font-semibold"
                  >
                    システムの要対応
                  </h3>
                </div>
              )}
              <ul>
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      className="hover:bg-muted/50 group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "h-2 w-2 shrink-0 rounded-full",
                          priorityClass[item.priority].dot
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="text-foreground block truncate text-sm font-medium">
                          {item.title}
                        </span>
                        <span className="text-muted-foreground block truncate text-xs">
                          {item.description}
                        </span>
                      </span>
                      <Tag
                        tone="neutral"
                        className={cn(
                          "shrink-0 tabular-nums",
                          priorityClass[item.priority].count
                        )}
                        size="compact"
                      >
                        {formatCount(item.count)}
                      </Tag>
                      <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {!loading &&
            !failed &&
            items.length === 0 &&
            memberTasks.length === 0 && (
              <div className="px-4 py-8 text-center">
                <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500/70" />
                <p className="text-foreground text-sm font-medium">
                  要対応はありません
                </p>
                <p className="text-muted-foreground  text-xs">
                  現在のタスクはすべて確認済みです
                </p>
              </div>
            )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
